import React, { useEffect, useState, useRef, useCallback } from 'react';
import Box from '@mui/material/Box';
import axios from 'axios';
import ProductFilter from '../components/ProductFilters';
import { ProductCard } from '../components/ProductCard';
import { useFilterControl } from '../store/Store';
import { BASE_URL } from '../apis/constant';
import ProductCardSkeleton from '../components/ProductCardSkeleton';

const PAGE_SIZE = 24;

const Shop = () => {
  const { appliedFilters } = useFilterControl();

  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Use refs to track state without triggering re-renders
  const observerRef = useRef(null);
  const pageLoadingRef = useRef(false);
  const lastProductIdRef = useRef(null);

  const fetchProducts = useCallback(async (pageNum) => {
    // Prevent multiple simultaneous requests
    if (pageLoadingRef.current) return;

    pageLoadingRef.current = true;
    setLoading(true);

    try {
      const response = await axios.post(`${BASE_URL}/api/get_shop_page_products/`, {
        ...appliedFilters,
        page: pageNum,
        page_size: PAGE_SIZE,
      });

      const newProducts = response.data.products || [];

      // Filter out duplicates based on product id
      setProducts(prev => {
        const existingIds = new Set(prev.map(p => p.id));
        const uniqueNewProducts = newProducts.filter(product =>
          !existingIds.has(product.id)
        );

        // Track the last product ID
        if (uniqueNewProducts.length > 0) {
          lastProductIdRef.current = uniqueNewProducts[uniqueNewProducts.length - 1].id;
        }

        return [...prev, ...uniqueNewProducts];
      });

      setHasMore(response.data.has_more);
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
      pageLoadingRef.current = false;
      setIsInitialLoad(false);
    }
  }, [appliedFilters]);

  // Reset everything when filters change
  useEffect(() => {
    setProducts([]);
    setPage(1);
    setHasMore(true);
    setIsInitialLoad(true);
    pageLoadingRef.current = false;
    lastProductIdRef.current = null;

    // Disconnect any existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
  }, [appliedFilters]);

  // Initial load and page change
  useEffect(() => {
    if (isInitialLoad || page > 1) {
      fetchProducts(page);
    }
  }, [page, fetchProducts, isInitialLoad]);

  // Infinite scroll setup with debouncing
  const lastProductRef = useCallback((node) => {
    if (loading || !hasMore) return;

    // Disconnect previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create new observer with debouncing
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !pageLoadingRef.current) {
          // Use setTimeout to debounce and prevent double trigger
          setTimeout(() => {
            if (!pageLoadingRef.current && hasMore) {
              setPage(prev => prev + 1);
            }
          }, 100);
        }
      },
      {
        rootMargin: '100px',
        threshold: 0.1,
      }
    );

    if (node) {
      observerRef.current.observe(node);
    }
  }, [loading, hasMore]);

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Enhanced ProductGrid with better duplicate prevention
  const ProductGrid = () => {
    // Create a map of product IDs to ensure uniqueness
    const uniqueProducts = [];
    const seenIds = new Set();

    for (const product of products) {
      if (!seenIds.has(product.id)) {
        seenIds.add(product.id);
        uniqueProducts.push(product);
      }
    }

    return (
      <div className="grid grid-cols-2 md:grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-2.5 sm:gap-5 justify-center">
        {uniqueProducts.map((product, index) => {
          const isLastProduct = index === uniqueProducts.length - 1;

          return (
            <ProductCard
              key={`${product.id}-${product.updated_at || product.created_at || index}`}
              product={product}
              ref={isLastProduct ? lastProductRef : null}
            />
          );
        })}

        {/* {loading && (
          <div className="col-span-full text-center mt-4 py-4">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            <p className="mt-2">Loading more products...</p>
          </div>
        )} */}

        {loading && (
          [...Array(8)].map((_, index) => (
            <ProductCardSkeleton key={index} />
          ))
        )}

        {!hasMore && uniqueProducts.length > 0 && (
          <p className="col-span-full text-center mt-4 py-4 text-gray-500">
            No more products to load
          </p>
        )}

        {!isInitialLoad && uniqueProducts.length === 0 && !loading && (
          <p className="col-span-full text-center mt-4 py-4 text-gray-500">
            No products found
          </p>
        )}
      </div>
    );
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Box sx={{
        width: { sm: '220px', md: '260px' },
        display: { xs: 'none', sm: 'block' },
        padding: '10px 0',
        position: 'sticky',
        top: 0,
        height: 'calc(100dvh - 72px)',
        overflowY: 'auto',
      }}>
        <ProductFilter />
      </Box>

      <Box sx={{
        flex: '1',
        padding: { xs: '10px 0', sm: '10px 20px' },
        minHeight: '100vh'
      }}>
        <section className="transition-colors duration-300">
          <div className="max-w-7xl mx-auto">
            <ProductGrid />
          </div>
        </section>
      </Box>
    </Box>
  );
};

export default Shop;